import { api } from './client';
import {
  orgDetailSchema,
  orgKeysSchema,
  orgRowSchema,
  orgViewSchema,
  transferPreviewSchema,
  transferResultSchema,
  type CreateDirectOrgRequest,
  type OrgDetail,
  type OrgKeys,
  type OrgRow,
  type OrgStatus,
  type OrgView,
  type TransferPreview,
  type TransferResult,
} from './orgTypes';
import {
  apiKeyViewSchema,
  issuedKeySchema,
  pageSchema,
  planViewSchema,
  type ApiKeyView,
  type IssueKeyRequest,
  type IssuedKey,
  type Page,
  type PlanView,
} from './types';

export interface ListOrgsParams {
  q?: string;
  status?: OrgStatus;
  partner_id?: string;
  plan_id?: string;
  page?: number;
}

const orgPageSchema = pageSchema(orgRowSchema);

export async function listOrgs(params: ListOrgsParams = {}): Promise<Page<OrgRow>> {
  return orgPageSchema.parse(await api.get<unknown>('/orgs', { query: { ...params } }));
}

export async function listDirectOrgs(
  params: { q?: string; status?: OrgStatus; page?: number } = {},
): Promise<Page<OrgRow>> {
  return orgPageSchema.parse(await api.get<unknown>('/direct-orgs', { query: { ...params } }));
}

export async function directPlanOptions(): Promise<PlanView[]> {
  return planViewSchema.array().parse(await api.get<unknown>('/direct-orgs/plan-options'));
}

export async function createDirectOrg(request: CreateDirectOrgRequest): Promise<OrgView> {
  return orgViewSchema.parse(await api.post<unknown>('/direct-orgs', request));
}

export async function fetchOrg(orgId: string): Promise<OrgDetail> {
  return orgDetailSchema.parse(await api.get<unknown>(`/orgs/${orgId}`));
}

export async function setOrgPlan(orgId: string, planId: string): Promise<OrgView> {
  return orgViewSchema.parse(await api.post<unknown>(`/orgs/${orgId}/plan`, { plan_id: planId }));
}

export async function previewTransfer(orgId: string): Promise<TransferPreview> {
  return transferPreviewSchema.parse(await api.get<unknown>(`/orgs/${orgId}/transfer`));
}

export async function transferOrg(
  orgId: string,
  toPartnerId: string,
  reason: string,
): Promise<TransferResult> {
  return transferResultSchema.parse(
    await api.post<unknown>(`/orgs/${orgId}/transfer`, { to_partner_id: toPartnerId, reason }),
  );
}

export async function fetchOrgKeys(orgId: string): Promise<OrgKeys> {
  return orgKeysSchema.parse(await api.get<unknown>(`/orgs/${orgId}/keys`));
}

export async function issueOrgKey(orgId: string, request: IssueKeyRequest): Promise<IssuedKey> {
  return issuedKeySchema.parse(await api.post<unknown>(`/orgs/${orgId}/keys`, request));
}

export async function setOrgKeyPermissions(
  orgId: string,
  keyId: string,
  permissions: string[],
): Promise<ApiKeyView> {
  return apiKeyViewSchema.parse(
    await api.post<unknown>(`/orgs/${orgId}/keys/${keyId}/permissions`, { permissions }),
  );
}

export async function revokeOrgKey(orgId: string, keyId: string): Promise<ApiKeyView> {
  return apiKeyViewSchema.parse(await api.post<unknown>(`/orgs/${orgId}/keys/${keyId}/revoke`));
}
